import { useEffect, useState } from "react";
import { track } from "../../analytics";
import type { MediaItem } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { userApi } from "../../lib/api";
import { useSession } from "../../lib/session";
import { useFapsStore } from "../../lib/faps";
import { cx } from "../ui/primitives";
import { AuthModal } from "./AuthModal";
import { CrownMark } from "../ui/Crown";
import { useCrownBursts } from "../../lib/useCrownBursts";

/**
 * TikTok/Instagram-style "fap" (like) control overlaid on the Goon Fuel video —
 * a translucent crown in the corner that fills gold once fapped. It's a spammable
 * like: every click adds the clip to your library (idempotent — never un-faps) and
 * launches one more rising crown, so rapid taps stack bursts. No count is shown.
 * Anonymous users get a sign-in prompt. Rendered inside the (relative) video stage.
 */
export function FapBar({ clip }: { clip: MediaItem }) {
  const user = useSession((s) => s.user);
  const [fapped, setFapped] = useState(false);
  const { bursts, pushBurst } = useCrownBursts();
  const [authOpen, setAuthOpen] = useState(false);
  const key = clipKey(clip);

  useEffect(() => {
    let cancelled = false;
    userApi
      .fapStatus(clip)
      .then((s) => !cancelled && setFapped(s.fapped))
      .catch(() => !cancelled && setFapped(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Spammable fap: always add-to-library + burst, never un-fap. The reply carries the
  // balance when the tap paid (under the day's cap) — the pill ticks, nothing floats.
  function fap() {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setFapped(true);
    userApi
      .fapClick(clip)
      .then((r) => {
        // The whole reply, paid or not: the store reads `capped` off a tap that paid nothing.
        useFapsStore.getState().applyClick(r);
      })
      .catch(() => undefined);
    track("fap_saved", { surface: "fapbar" });
    pushBurst();
  }

  return (
    <>
      <div className="absolute bottom-3 right-3 z-20">
        <button
          type="button"
          onClick={fap}
          aria-pressed={fapped}
          title={!user ? "Sign in to fap" : "Fap — save to your library"}
          className={cx(
            "flex h-12 w-12 items-center justify-center rounded-full shadow-lg backdrop-blur-sm transition-all duration-150 hover:scale-110 hover:opacity-100",
            fapped ? "bg-[#f5c518]/25 opacity-100" : "bg-black/45 opacity-70",
          )}
        >
          <CrownMark
            size={26}
            tone={fapped ? "gold" : "muted"}
            className="drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]"
          />
        </button>
      </div>

      {/* Floating-crown bursts, rising from the crown's corner */}
      <div className="pointer-events-none absolute inset-0 z-20">
        {bursts.map((id) => (
          <div
            key={id}
            className="absolute bottom-3 right-3 animate-[fapFloat_0.9s_ease-out_forwards]"
          >
            <CrownMark
              size={26}
              className="drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]"
            />
          </div>
        ))}
      </div>

      {authOpen && (
        <AuthModal
          initialMode="register"
          reason="Create a free account to fap this clip."
          onClose={() => setAuthOpen(false)}
        />
      )}
    </>
  );
}
